/**
 * Date de livraison estimée sur les cartes produit.
 *
 * Liquid ne sait pas compter des jours ouvrés proprement, et une date rendue
 * côté serveur serait fausse dès le lendemain matin puisque la page est mise
 * en cache. Le serveur rend donc un repli statique, que ce module remplace.
 *
 * Les durées viennent des réglages de thème, jamais du code : le client les
 * ajustera quand il connaîtra les délais réels de l'atelier. Les attributs
 * `data-*` et la phrase de repli sont produits par
 * `snippets/lrs-delivery-window.liquid`, qui fait l'addition une seule fois
 * pour les trois endroits qui l'affichent.
 *
 * PRODUCTION A UN MINIMUM ET UN MAXIMUM. Avec un nombre unique, la borne
 * haute valait production + acheminement maximum, soit 6 jours, quand les
 * pages écrites annonçaient 7. La carte promettait donc une date plus tôt que
 * la promesse officielle. Les deux bornes se calculent maintenant chacune avec
 * la sienne.
 */

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** Avance de `n` jours ouvrés, samedis et dimanches sautés. */
function addBusinessDays(from, n) {
  const d = new Date(from.getTime());
  let left = Math.max(0, n);
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  // Si le point de départ tombe un week-end, on se cale sur le lundi.
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d;
}

const num = (el, key, fallback) => {
  const v = parseInt(el.dataset[key] || '', 10);
  return Number.isFinite(v) && v >= 0 ? v : fallback;
};

function label(date, withMonth) {
  const jour = JOURS[date.getDay()];
  const n = date.getDate();
  return withMonth ? `${jour} ${n} ${MOIS[date.getMonth()]}` : `${jour} ${n}`;
}

function fill(el) {
  const out = el.querySelector('[data-lrs-delivery-text]');
  if (!out) return;

  const prodMin = num(el, 'prodMin', 2);
  const prodMax = num(el, 'prodMax', 3);
  const shipMin = num(el, 'shipMin', 2);
  const shipMax = num(el, 'shipMax', 4);

  // Chaque borne avec la sienne : le plus tôt possible d'un côté, le plus tard
  // possible de l'autre. Un réglage mal saisi (max plus petit que min) donnerait
  // une fourchette à l'envers, d'où les deux garde-fous.
  const low = Math.min(prodMin + shipMin, prodMax + shipMax);
  const high = Math.max(prodMin + shipMin, prodMax + shipMax);

  const today = new Date();
  const first = addBusinessDays(today, low);
  const last = addBusinessDays(today, high);

  // Le mois n'est répété sur la première date que s'il diffère de la seconde.
  const sameMonth = first.getMonth() === last.getMonth();
  const a = label(first, !sameMonth);
  const b = label(last, true);

  // La fiche produit ouvre la phrase (« Commandé aujourd'hui, ») : « Chez »
  // perd alors sa majuscule, qui n'aurait rien à faire au milieu d'une phrase.
  const prefix = (el.dataset.lrsPrefix || '').trim();
  const head = prefix ? 'chez vous entre le' : 'Chez vous entre le';

  // Les dates en gras sur la fiche, en texte simple sur les cartes. Les deux
  // chaînes sont construites à partir des tableaux de ce fichier et de nombres
  // entiers : rien de saisi n'entre dans le balisage.
  // « oct. » porte déjà son point : en ajouter un second ferait « oct.. ».
  const stop = b.endsWith('.') ? '' : '.';

  if (el.hasAttribute('data-lrs-rich')) {
    const sentence = `${head} <b>${a}</b> et le <b>${b}</b>${stop}`;
    out.innerHTML = prefix ? `${escapeText(prefix)} ${sentence}` : sentence;
    return;
  }

  out.textContent = prefix ? `${prefix} ${head} ${a} et le ${b}${stop}` : `${head} ${a} et le ${b}`;
}

/** Le préfixe vient d'un réglage de thème : il est échappé avant le balisage. */
function escapeText(value) {
  const box = document.createElement('span');
  box.textContent = value;
  return box.innerHTML;
}

function scan(root) {
  (root || document).querySelectorAll('[data-lrs-delivery]').forEach((el) => {
    if (el.dataset.lrsFilled === 'on') return;
    el.dataset.lrsFilled = 'on';
    fill(el);
  });
}

scan(document);

// Les cartes arrivent aussi par filtrage, pagination et recommandations.
const observer = new MutationObserver((records) => {
  for (const r of records) {
    for (const node of r.addedNodes) {
      if (node.nodeType === 1) scan(node);
    }
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', (event) => scan(event.target));
